import moment from "moment";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { getTxExplorerUrl } from "../../utils/chain";
import { getTimeAgo, nanosToSeconds } from "../../utils/date";
import { ExplorerLink } from "./style";
import RowActionButton from "../shared/RowActionButton";
import { Align } from "../shared/Table/style";
import { titleFor } from "./helpers";
import { ColumnContext, TimestampColumnOptions } from "./types";

export function buildTimestampColumn<T>(
  { t, tooltips, translateAll }: ColumnContext,
  { accessor, titleKey = "time", unit }: TimestampColumnOptions
): ITableColumn<T> {
  return {
    title: titleFor(t, titleKey),
    accessor: accessor as any,
    tooltip: tooltips("tradeTimestamp"),
    Cell: ({ value }) => {
      if (value === undefined || value === null || value === "") return "-";
      const seconds =
        unit === "nanos" ? nanosToSeconds(String(value)) : Number(value);
      if (!Number.isFinite(seconds)) return "-";
      const ts = moment.unix(seconds);
      // Absolute local time primary; relative "5m ago" on hover.
      return (
        <span title={getTimeAgo(ts, translateAll)}>
          {ts.format("DD MMM YY HH:mm:ss")}
        </span>
      );
    },
  };
}

export function buildActionsColumn<T extends object>(
  rowIdAccessor: string,
  onClick: (row: T) => void,
  /** Field holding this row's tx hash. Adds a link to the chain's explorer. */
  txHashAccessor?: keyof T
): ITableColumn<T> {
  return {
    title: " ",
    accessor: rowIdAccessor as any,
    Header: () => <div />,
    Cell: ({ row }) => {
      const txHash = txHashAccessor
        ? (row.original as T)[txHashAccessor]
        : undefined;
      const txUrl = txHash ? getTxExplorerUrl(String(txHash)) : "";
      return (
        <Align align="right">
          {/* A real link: focusable, openable in a new tab, and it stops the
              click from also opening the row's details modal. */}
          {txUrl ? (
            <ExplorerLink
              href={txUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View transaction on block explorer"
              title="View transaction on block explorer"
              onClick={(e) => e.stopPropagation()}
            >
              ↗
            </ExplorerLink>
          ) : null}
          <RowActionButton
            variant="details"
            onClick={(e) => {
              e.stopPropagation();
              onClick(row.original as T);
            }}
          />
        </Align>
      );
    },
  };
}
